import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { httpGet, httpPost, httpDelete } from "../helpers/httpMethods";

// Papers this reader follows. One GET /me/following per sign-in, shared by every follow button, the home page
// (followed papers first) and My library → Following. Changes are announced with the "tns:follow" event.
let cache = { token: null, promise: null, ids: new Set() };
const tokenNow = () => { try { return localStorage.getItem("token") || ""; } catch (e) { return ""; } };
const announce = () => window.dispatchEvent(new Event("tns:follow"));

export function loadFollowing(force) {
  const token = tokenNow();
  if (!token) { cache = { token: null, promise: null, ids: new Set() }; return Promise.resolve(cache.ids); }
  if (force || cache.token !== token || !cache.promise) {
    cache = {
      token,
      ids: cache.token === token ? cache.ids : new Set(),
      promise: httpGet("me/following").then((r) => {
        const ids = new Set(((r && r.data && r.data.ids) || []).map(String));
        if (cache.token === token) { cache.ids = ids; announce(); }
        return ids;
      }).catch(() => cache.ids),
    };
  }
  return cache.promise;
}

export const isFollowing = (id) => cache.ids.has(String(id));

// Follow or unfollow, with the button updating at once and undone if the server says no.
export async function toggleFollow(id, name) {
  const key = String(id);
  const was = cache.ids.has(key);
  const label = name ? String(name).trim() : "this paper";
  if (was) cache.ids.delete(key); else cache.ids.add(key);
  announce();
  const r = was ? await httpDelete(`follow/${key}`) : await httpPost(`follow/${key}`);
  if (!r || r.er) {
    if (was) cache.ids.add(key); else cache.ids.delete(key);
    announce();
    NotificationManager.error("That didn’t work. Please try again.");
    return was;
  }
  if (was) NotificationManager.info(`You’ve unfollowed ${label}.`);
  else NotificationManager.success(`You’re following ${label}. It shows first on your home page and in My library → Following.`);
  return !was;
}

// [followedIdsSet, version]: re-renders whenever anything is followed or unfollowed.
export function useFollowing() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("tns:follow", bump);
    loadFollowing();
    return () => window.removeEventListener("tns:follow", bump);
  }, []);
  return cache.ids;
}
